import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { Stack } from '@mui/system';
import { ViewQuiltOutlined } from '@mui/icons-material';
import { JourneyTemplateTile } from './JourneyTemplateTile';
import { recommendedJourneyTemplateIds } from './journeyTemplates';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { graphql } from '../../gql';
import { useApiQuery } from '../../query/hooks';

const journey_listJourneyTemplates = graphql(`
	query journey_listJourneyTemplates {
		listJourney(filter: { isTemplate: { eq: true } }) {
			id
			...journeyTemplateTile_journeyFragment
		}
	}
`);

type Props = Readonly<{
	setSelectedJourenyId: (templateId: string) => void;
	setTemplateName: (templateName: string) => void;
	selectedTemplateId?: string | null;
}>;

const CreateJourneyTemplates: FC<Props> = ({ setSelectedJourenyId, setTemplateName, selectedTemplateId }) => {
	const theme = useTheme();
	const { data, isLoading } = useApiQuery(journey_listJourneyTemplates);

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (data == null) {
		return null;
	}

	const { listJourney } = data;

	const recommendedTemplates = listJourney.filter((journey) => recommendedJourneyTemplateIds.includes(journey.id));
	const otherTemplates = listJourney.filter((journey) => !recommendedJourneyTemplateIds.includes(journey.id));

	return (
		<Stack
			direction={'column'}
			width={'100%'}
			gap={5.5}
			paddingX={{ xs: 3, sm: '60px' }}
			paddingTop={{ xs: 4, sm: 17.5 }}
			paddingBottom={{ xs: 2 }}
			marginTop={{ sm: -14.5 }}
			borderLeft={{ sm: `1px solid ${theme.palette.grey[200]}` }}
			sx={{ backgroundColor: theme.palette.grey[50] }}
		>
			<Stack gap={2}>
				<Typography variant={'h3'} fontWeight={700}>
					<FormattedMessage id={'form.journey.create.template.title'} />
				</Typography>
				<Typography variant={'h5'}>
					<FormattedMessage id={'form.journey.create.template.subtitle'} />
				</Typography>
			</Stack>
			{Boolean(recommendedTemplates.length) && (
				<Stack direction={'column'} gap={1}>
					<Typography variant={'h5'} fontWeight={500} color={theme.palette.grey[600]}>
						<FormattedMessage id={'form.journey.create.template.recommended.title'} />
					</Typography>
					<Stack direction={'row'} width={'100%'} gap={2}>
						<Grid container spacing={2}>
							{recommendedTemplates.map((templateJourney) => {
								return (
									<Grid item key={templateJourney.id} xs={12} sm={12} md={12} lg={4}>
										<JourneyTemplateTile
											setSelectedJourneyId={setSelectedJourenyId}
											setTemplateName={setTemplateName}
											icon={<ViewQuiltOutlined />}
											isActive={templateJourney.id === selectedTemplateId}
											journeyData={templateJourney}
										/>
									</Grid>
								);
							})}
						</Grid>
					</Stack>
				</Stack>
			)}

			{Boolean(otherTemplates.length) && (
				<Stack direction={'column'} gap={1}>
					<Typography variant={'h5'} fontWeight={500} color={theme.palette.grey[600]}>
						<FormattedMessage id={'form.journey.create.template.allTypes.title'} />
					</Typography>
					<Stack width={'100%'} gap={2}>
						<Grid container spacing={2}>
							{otherTemplates.map((templateJourney) => {
								return (
									<Grid item key={templateJourney.id} xs={12} sm={12} md={6} lg={4}>
										<JourneyTemplateTile
											setSelectedJourneyId={setSelectedJourenyId}
											setTemplateName={setTemplateName}
											icon={<ViewQuiltOutlined />}
											iconOnTop={true}
											isActive={templateJourney.id === selectedTemplateId}
											journeyData={templateJourney}
										/>
									</Grid>
								);
							})}
						</Grid>
					</Stack>
				</Stack>
			)}
		</Stack>
	);
};

export { CreateJourneyTemplates };
